import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { getTemplatesForTaskType } from "../api/checklistTemplateApi";
import {
  getTaskChecklist,
  applyTemplateToTask,
  addAdhocItem,
  toggleChecklistItem,
} from "../api/taskChecklistApi";

/* ─── Scoped styles (Jira-style checklist look) ─── */
const STYLES = `
  .tcl-trigger-wrap { display: flex; justify-content: flex-start; margin-bottom: 12px; }
  .tcl-trigger {
    display: inline-flex; align-items: center; justify-content: center;
    padding: 6px 14px; flex-shrink: 0;
    background: #f7f8fa; border: 1px solid #e4e6eb; border-radius: 8px; color: #4c5fd5;
    font-size: 12.5px; font-weight: 600;
    cursor: pointer; transition: background 0.12s ease, border-color 0.12s ease;
  }
  .tcl-trigger:hover { background: #eef0f4; border-color: #c1c7d0; }

  .tcl { border: 1px solid #eef0f4; border-radius: 10px; background: #fff; }

  .tcl-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 14px; cursor: pointer; user-select: none;
    transition: background 0.12s ease;
    border-radius: 10px 10px 0 0;
  }
  .tcl-head:hover { background: #fafbfc; }

  .tcl-head-left { display: flex; align-items: center; gap: 9px; min-width: 0; }
  .tcl-icon-badge {
    width: 26px; height: 26px; border-radius: 7px; flex-shrink: 0;
    background: #eef2ff; color: #4c5fd5; display: flex; align-items: center; justify-content: center;
    font-size: 13px;
  }
  .tcl-title {
    font-size: 13px; font-weight: 700; color: #172b4d;
    text-transform: uppercase; letter-spacing: .06em; margin: 0;
    display: flex; align-items: center; gap: 8px; white-space: nowrap;
  }
  .tcl-count {
    font-size: 11.5px; font-weight: 600; color: #6b7280;
    background: #f1f2f5; padding: 2px 8px; border-radius: 10px;
    text-transform: none; letter-spacing: 0;
  }
  .tcl-count.done { background: #e3fcef; color: #1a7f5a; }
  .tcl-chevron { color: #97a0af; font-size: 15px; transition: transform 0.18s ease; flex-shrink: 0; }
  .tcl-chevron.open { transform: rotate(180deg); }

  .tcl-progress-track {
    height: 5px; background: #f1f2f5; border-radius: 3px; overflow: hidden; margin: 0 14px 12px;
  }
  .tcl-progress-fill {
    height: 100%; border-radius: 3px; transition: width 0.25s ease;
    background: linear-gradient(90deg, #4c9aff, #36b37e);
  }
  .tcl-progress-fill.complete { background: #36b37e; }

  .tcl-body { padding: 2px 14px 14px; }

  .tcl-loading { display: flex; align-items: center; gap: 8px; padding: 14px 0; color: #8b93a7; font-size: 13px; }
  .tcl-spin {
    width: 14px; height: 14px; border: 2px solid #e5e7eb; border-top-color: #8b93a7;
    border-radius: 50%; animation: tcl-spin 0.7s linear infinite;
  }
  @keyframes tcl-spin { to { transform: rotate(360deg); } }

  .tcl-template-row { margin-bottom: 12px; position: relative; }
  .tcl-template-bar {
    display: flex; align-items: center; gap: 8px;
    background: #f7f8fa; border: 1px solid #e4e6eb; border-radius: 8px;
    padding: 8px 6px 8px 12px; transition: border-color 0.15s;
  }
  .tcl-template-bar:hover { border-color: #c1c7d0; }
  .tcl-template-bar-icon { color: #97a0af; font-size: 13px; flex-shrink: 0; }
  .tcl-template-name { flex: 1; font-size: 13px; color: #172b4d; font-weight: 600; }
  .tcl-template-name.placeholder { color: #97a0af; font-weight: 400; }

  .tcl-kebab-btn {
    width: 26px; height: 26px; border-radius: 6px; border: none; background: transparent;
    color: #6b7280; font-size: 15px; cursor: pointer; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; transition: background 0.12s ease;
  }
  .tcl-kebab-btn:hover { background: #e9ebef; color: #172b4d; }
  .tcl-kebab-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .tcl-template-menu {
    position: absolute; top: calc(100% + 6px); right: 0; min-width: 230px; max-width: 320px;
    max-height: 240px; overflow-y: auto; background: #fff; border: 1px solid #e4e6eb;
    border-radius: 9px; box-shadow: 0 8px 24px rgba(23,43,77,0.15); padding: 6px; z-index: 50;
    animation: tcl-menu-in 0.14s ease;
  }
  @keyframes tcl-menu-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }

  .tcl-template-menu-caption {
    font-size: 10.5px; font-weight: 700; color: #97a0af; text-transform: uppercase;
    letter-spacing: .05em; padding: 6px 8px 4px;
  }
  .tcl-template-menu-item {
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
    padding: 8px 9px; border-radius: 6px; font-size: 13px; color: #172b4d;
    cursor: pointer; transition: background 0.1s ease;
  }
  .tcl-template-menu-item:hover { background: #f1f2f5; }
  .tcl-template-menu-item.active { background: #eef2ff; color: #4c5fd5; font-weight: 600; }
  .tcl-template-menu-item i { color: #4c5fd5; font-size: 13px; }
  .tcl-template-menu-tag {
    font-size: 10px; font-weight: 600; color: #97a0af; background: #f1f2f5;
    padding: 1px 6px; border-radius: 8px; flex-shrink: 0;
  }
  .tcl-template-menu-empty { padding: 10px 9px; font-size: 12.5px; color: #97a0af; }

  .tcl-based-on { display: inline-block; margin-top: 6px; font-size: 11px; color: #97a0af; }

  .tcl-empty {
    display: flex; flex-direction: column; align-items: center; text-align: center;
    padding: 18px 10px; color: #8b93a7;
  }
  .tcl-empty i { font-size: 22px; color: #c1c7d0; margin-bottom: 6px; }
  .tcl-empty-text { font-size: 12.5px; }

  .tcl-list {
    list-style: none; margin: 4px 0 10px; padding: 0 4px 0 0;
    max-height: 240px; overflow-y: auto;
    scrollbar-width: thin; scrollbar-color: #c1c7d0 transparent;
  }
  .tcl-list::-webkit-scrollbar { width: 6px; }
  .tcl-list::-webkit-scrollbar-track { background: transparent; }
  .tcl-list::-webkit-scrollbar-thumb { background: #c1c7d0; border-radius: 10px; }
  .tcl-list::-webkit-scrollbar-thumb:hover { background: #97a0af; }
  .tcl-item {
    display: flex; align-items: center; gap: 10px; padding: 6px 6px;
    border-radius: 6px; transition: background 0.12s ease;
  }
  .tcl-item:hover { background: #f7f8fa; }

  .tcl-checkbox {
    width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0;
    border: 2px solid #c1c7d0; background: #fff; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.15s, border-color 0.15s;
    appearance: none; -webkit-appearance: none; position: relative;
  }
  .tcl-checkbox:checked { background: #36b37e; border-color: #36b37e; }
  .tcl-checkbox:checked::after {
    content: ""; position: absolute; left: 5px; top: 1px;
    width: 4px; height: 8px; border: solid #fff; border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
  .tcl-checkbox:hover { border-color: #36b37e; }

  .tcl-item-text { flex: 1; font-size: 13.5px; color: #172b4d; line-height: 1.4; word-break: break-word; }
  .tcl-item-text.checked { text-decoration: line-through; color: #97a0af; }

  .tcl-tag-custom {
    font-size: 10px; font-weight: 600; color: #6b7280; background: #f1f2f5;
    padding: 2px 7px; border-radius: 10px; flex-shrink: 0; white-space: nowrap;
  }

  .tcl-add-row { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
  .tcl-add-icon {
    width: 18px; height: 18px; border-radius: 50%; border: 2px dashed #c1c7d0;
    display: flex; align-items: center; justify-content: center; color: #97a0af;
    font-size: 11px; flex-shrink: 0;
  }
  .tcl-add-input {
    flex: 1; border: none; border-bottom: 1px dashed #d5d9e0; background: transparent;
    font-size: 13.5px; padding: 5px 2px; color: #172b4d; transition: border-color 0.15s;
  }
  .tcl-add-input:focus { outline: none; border-bottom: 1px solid #4c9aff; }
  .tcl-add-input::placeholder { color: #a5abba; }
  .tcl-add-btn {
    font-size: 12px; font-weight: 600; color: #4c5fd5; background: #eef2ff;
    border: none; border-radius: 6px; padding: 5px 12px; cursor: pointer;
    transition: background 0.12s ease; white-space: nowrap;
  }
  .tcl-add-btn:hover { background: #dfe4ff; }
  .tcl-add-btn:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/**
 * Collapsible, lazy-loaded checklist section for the task detail view.
 * Nothing is fetched or rendered until the user expands it.
 * Shared across AdminViewTasks.js, ViewTasks.js, ViewTaskss.js, Tasks.js.
 */
export default function TaskChecklistSection({ activeTask }) {
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checklist, setChecklist] = useState(null); // TaskChecklistDto or null
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [pendingTemplateId, setPendingTemplateId] = useState(null); // set while confirm dialog is open
  const [newItemText, setNewItemText] = useState("");
  const [applying, setApplying] = useState(false);
  const [templateMenuOpen, setTemplateMenuOpen] = useState(false);
  const templateMenuRef = useRef(null);

  const taskId = activeTask?.id;
  const taskType = activeTask?.taskType;

  // Close the template switcher menu on outside click
  useEffect(() => {
    if (!templateMenuOpen) return;
    const handleClickOutside = (e) => {
      if (templateMenuRef.current && !templateMenuRef.current.contains(e.target)) {
        setTemplateMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [templateMenuOpen]);

  // Reset when a different task is opened
  useEffect(() => {
    setExpanded(false);
    setLoaded(false);
    setLoading(false);
    setChecklist(null);
    setTemplates([]);
    setSelectedTemplateId("");
    setPendingTemplateId(null);
    setNewItemText("");
  }, [taskId]);

  const loadChecklistData = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const [checklistData, templatesData] = await Promise.all([
        getTaskChecklist(taskId),
        getTemplatesForTaskType(taskType),
      ]);
      setChecklist(checklistData || null);
      setTemplates(Array.isArray(templatesData) ? templatesData : []);
      setSelectedTemplateId(checklistData?.sourceTemplateId ? String(checklistData.sourceTemplateId) : "");
    } catch (err) {
      toast.error("Failed to load checklist.");
    } finally {
      setLoading(false);
    }
  }, [taskId, taskType]);

  useEffect(() => {
    if (expanded && taskId && !loaded) {
      setLoaded(true);
      loadChecklistData();
    }
  }, [expanded, taskId, loaded, loadChecklistData]);

  const items = checklist?.items || [];
  const hasItems = items.length > 0;
  const checkedCount = items.filter((i) => i.checked).length;
  const total = items.length;
  const percent = total > 0 ? Math.round((checkedCount / total) * 100) : 0;
  const allDone = total > 0 && checkedCount === total;
  const hasTemplateApplied = hasItems || !!checklist?.sourceTemplateName;

  const doApplyTemplate = async (templateId) => {
    setApplying(true);
    try {
      const updated = await applyTemplateToTask(taskId, Number(templateId));
      setChecklist(updated);
      setSelectedTemplateId(String(templateId));
      toast.success("Checklist template applied.");
    } catch (err) {
      toast.error("Failed to apply template.");
    } finally {
      setApplying(false);
      setPendingTemplateId(null);
    }
  };

  const selectTemplate = (templateId) => {
    setTemplateMenuOpen(false);
    if (!templateId || String(templateId) === selectedTemplateId) return;
    if (hasItems) {
      // Switching away from an existing (possibly checked) checklist — confirm first
      setPendingTemplateId(String(templateId));
    } else {
      doApplyTemplate(templateId);
    }
  };

  const handleToggleItem = async (itemId) => {
    try {
      const updated = await toggleChecklistItem(taskId, itemId);
      setChecklist(updated);
    } catch {
      toast.error("Failed to update checklist item.");
    }
  };

  const handleAddAdhocItem = async () => {
    const text = newItemText.trim();
    if (!text) return;
    try {
      const updated = await addAdhocItem(taskId, text);
      setChecklist(updated);
      setNewItemText("");
    } catch {
      toast.error("Failed to add checklist item.");
    }
  };

  const pendingTemplateName = templates.find((t) => String(t.id) === String(pendingTemplateId))?.name;

  return (
    <>
      <style>{STYLES}</style>

      {!expanded ? (
        <div className="tcl-trigger-wrap">
          <button type="button" className="tcl-trigger" onClick={() => setExpanded(true)}>
            Checklist
          </button>
        </div>
      ) : (
      <div className="task-section task-section--checklist">
      <div className="tcl">
        <div className="tcl-head" onClick={() => setExpanded(false)}>
          <div className="tcl-head-left">
            <span className="tcl-icon-badge">
              <i className="bi bi-list-task"></i>
            </span>
            <h6 className="tcl-title">
              Checklist
              {hasItems && (
                <span className={`tcl-count${allDone ? " done" : ""}`}>
                  {checkedCount}/{total}
                </span>
              )}
            </h6>
          </div>
          <i className="bi bi-chevron-up tcl-chevron open"></i>
        </div>

        {hasItems && (
          <div className="tcl-progress-track">
            <div className={`tcl-progress-fill${allDone ? " complete" : ""}`} style={{ width: `${percent}%` }} />
          </div>
        )}

          <div className="tcl-body">
            {loading ? (
              <div className="tcl-loading">
                <span className="tcl-spin" />
                Loading checklist…
              </div>
            ) : (
              <>
                <div className="tcl-template-row" ref={templateMenuRef}>
                  <div className="tcl-template-bar">
                    <i className="bi bi-bookmark tcl-template-bar-icon"></i>
                    <span className={`tcl-template-name${checklist?.sourceTemplateName ? "" : " placeholder"}`}>
                      {checklist?.sourceTemplateName || "No template applied"}
                    </span>
                    <button
                      type="button"
                      className="tcl-kebab-btn"
                      title="Choose or switch template"
                      disabled={applying}
                      onClick={() => setTemplateMenuOpen((o) => !o)}
                    >
                      <i className="bi bi-three-dots"></i>
                    </button>

                    {templateMenuOpen && (
                      <div className="tcl-template-menu">
                        <div className="tcl-template-menu-caption">
                          {hasItems ? "Switch template" : "Apply a template"}
                        </div>
                        {templates.length === 0 ? (
                          <div className="tcl-template-menu-empty">No templates for this task type</div>
                        ) : (
                          templates.map((t) => (
                            <div
                              key={t.id}
                              className={`tcl-template-menu-item${
                                String(t.id) === selectedTemplateId ? " active" : ""
                              }`}
                              onClick={() => selectTemplate(t.id)}
                            >
                              <span>{t.name}</span>
                              {t.isGeneral && <span className="tcl-template-menu-tag">General</span>}
                              {String(t.id) === selectedTemplateId && <i className="bi bi-check2"></i>}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {hasItems ? (
                  <ul className="tcl-list">
                    {items.map((item) => (
                      <li key={item.id} className="tcl-item">
                        <input
                          type="checkbox"
                          className="tcl-checkbox"
                          checked={item.checked}
                          onChange={() => handleToggleItem(item.id)}
                        />
                        <span className={`tcl-item-text${item.checked ? " checked" : ""}`}>{item.text}</span>
                        {item.isAdhoc && <span className="tcl-tag-custom">custom</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="tcl-empty">
                    <i className="bi bi-clipboard-check"></i>
                    <span className="tcl-empty-text">
                      {hasTemplateApplied
                        ? "This checklist has no items yet."
                        : "Apply a template above to start your checklist."}
                    </span>
                  </div>
                )}

                {hasTemplateApplied && (
                <div className="tcl-add-row">
                  <span className="tcl-add-icon">
                    <i className="bi bi-plus"></i>
                  </span>
                  <input
                    type="text"
                    className="tcl-add-input"
                    placeholder="Add a checklist point…"
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddAdhocItem();
                      }
                    }}
                  />
                  {newItemText.trim() && (
                    <button className="tcl-add-btn" onClick={handleAddAdhocItem}>
                      Add
                    </button>
                  )}
                </div>
                )}
              </>
            )}
          </div>
      </div>
      </div>
      )}

      {pendingTemplateId && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setPendingTemplateId(null)}></div>
          <div className="modal show fade d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header border-bottom bg-light">
                  <h6 className="modal-title fw-semibold mb-0">Switch checklist template?</h6>
                  <button type="button" className="btn-close" onClick={() => setPendingTemplateId(null)}></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    Switching to <strong>{pendingTemplateName}</strong> will replace all current checklist items and
                    their checked state. This can't be undone.
                  </p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setPendingTemplateId(null)}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={applying}
                    onClick={() => doApplyTemplate(pendingTemplateId)}
                  >
                    {applying ? "Applying…" : "Switch Template"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
